import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { 
  JobCard, 
  JobCardWithDetails, 
  CreateJobCardRequest, 
  UpdateJobCardRequest, 
  JobCardFilters,
  StartJobRequest,
  PauseJobRequest,
  ResumeJobRequest,
  CompleteJobRequest,
  ReportIssueRequest,
  RecordJobCardActivityRequest,
  JobCardActivity
} from '@/types/jobCards';
import { useToast } from '@/hooks/useToast';
import { useUser } from '@/hooks/useUser';

interface TimeEntryRequest {
  job_card_id: string;
  worker_id: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  notes?: string;
}

export const useJobCards = () => {
  const [jobCards, setJobCards] = useState<JobCardWithDetails[]>([]);
  const [selectedJobCard, setSelectedJobCard] = useState<JobCardWithDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useUser(); // Call useUser hook at the top level

  /**
   * Fetch job cards with optional filtering
   */
  const fetchJobCards = async (filters?: JobCardFilters) => {
    try {
      setLoading(true);
      
      // Use the job_cards_with_details view instead of constructing complex joins
      let query = supabase
        .from('job_cards_with_details')
        .select('*');
      
      // Apply filters
      if (filters) {
        if (filters.work_order_id) {
          query = query.eq('work_order_id', filters.work_order_id);
        }
        
        if (filters.worker_id) {
          query = query.eq('worker_id', filters.worker_id);
        }
        
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        
        if (filters.project_id) {
          query = query.eq('project_id', filters.project_id);
        }
        
        if (filters.from_date) {
          query = query.gte('planned_start_time', filters.from_date);
        }
        
        if (filters.to_date) {
          query = query.lte('planned_end_time', filters.to_date);
        }
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      setJobCards(data || []);
      return data;
    } catch (error) {
      console.error('Error fetching job cards:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch job cards. Please try again.',
        variant: 'destructive'
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch a single job card by ID
   */
  const fetchJobCardById = async (id: string) => {
    try {
      setLoading(true);
      
      // Use our RLS bypass function for more reliable fetching
      const { data, error } = await supabase
        .rpc('get_job_card_by_id', { job_card_id: id });
      
      if (error) {
        console.error('Error in RPC call:', error);
        throw error;
      }
      
      // The RPC returns an array, so we need to get the first item
      const jobCardData = data && data.length > 0 ? data[0] : null;
      
      if (!jobCardData) {
        console.error('No job card found with id:', id);
        throw new Error('Job card not found');
      }
      
      // Also fetch time entries
      const { data: timeEntries, error: timeEntriesError } = await supabase
        .from('time_entries')
        .select('*')
        .eq('job_card_id', id)
        .order('created_at', { ascending: false });
      
      if (timeEntriesError) {
        console.error('Error fetching time entries:', timeEntriesError);
      }
      
      // Add time entries to job card data
      const jobCardWithTimeEntries = {
        ...data,
        time_entries: timeEntries || []
      };
      
      setSelectedJobCard(jobCardWithTimeEntries || null);
      return jobCardWithTimeEntries;
    } catch (error) {
      console.error('Error fetching job card:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch job card details. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create a new job card
   */
  const createJobCard = async (jobCardData: CreateJobCardRequest) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('job_cards')
        .insert(jobCardData)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'Job card created successfully.',
      });
      
      return data;
    } catch (error) {
      console.error('Error creating job card:', error);
      toast({
        title: 'Error',
        description: 'Failed to create job card. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update an existing job card
   */
  const updateJobCard = async (id: string, jobCardData: UpdateJobCardRequest) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('job_cards')
        .update(jobCardData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'Job card updated successfully.',
      });
      
      return data;
    } catch (error) {
      console.error('Error updating job card:', error);
      toast({
        title: 'Error',
        description: 'Failed to update job card. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a job card
   */
  const deleteJobCard = async (id: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('job_cards')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'Job card deleted successfully.',
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting job card:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete job card. Please try again.',
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Start a job card with the enhanced flow
   */
  const startJob = async (data: StartJobRequest) => {
    try {
      setLoading(true);
      const userId = user?.id;
      console.log('startJob - User ID:', userId);
      console.log('startJob - User object:', user);
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Debug session context
      const { data: { session } } = await supabase.auth.getSession();
      console.log('startJob - Current session:', session);
      
      // First update the job card - use actual_start_time instead of actual_start_date
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .update({
          status: 'In Progress',
          actual_start_time: new Date().toISOString(),
          quantity: data.planned_quantity
        })
        .eq('id', data.job_card_id)
        .select()
        .single();
      
      if (jobCardError) {
        throw jobCardError;
      }
      
      // Then record the activity
      await recordActivity({
        job_card_id: data.job_card_id,
        activity_type: 'start',
        description: data.notes || 'Job started',
        quantity: data.planned_quantity
      });
      
      toast({
        title: 'Success',
        description: 'Job started successfully.',
      });
      
      return jobCardData;
    } catch (error) {
      console.error('Error starting job:', error);
      toast({
        title: 'Error',
        description: 'Failed to start job. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Pause a job
   */
  const pauseJob = async (data: PauseJobRequest) => {
    try {
      setLoading(true);
      const userId = user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Update the job card
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .update({
          status: 'Paused',
          quantity_completed: data.quantity_completed
        })
        .eq('id', data.job_card_id)
        .select()
        .single();
      
      if (jobCardError) {
        throw jobCardError;
      }
      
      // Record the activity
      await recordActivity({
        job_card_id: data.job_card_id,
        activity_type: 'pause',
        description: `Job paused. Reason: ${data.downtime_reason || data.notes || 'Not specified'}`,
        quantity: data.quantity_completed,
        metadata: { 
          downtime_category: data.downtime_category,
          downtime_reason: data.downtime_reason
        }
      });
      
      // Create a time entry for the paused period
      if (jobCardData.actual_start_time) {
        await addTimeEntry({
          job_card_id: data.job_card_id,
          worker_id: jobCardData.worker_id,
          start_time: jobCardData.actual_start_time,
          end_time: new Date().toISOString(),
          duration_minutes: calculateDurationMinutes(jobCardData.actual_start_time, new Date()),
          notes: data.notes,
          downtime_reason: data.downtime_reason,
          downtime_category: data.downtime_category,
          quantity_produced: data.quantity_completed
        });
      }
      
      toast({
        title: 'Success',
        description: 'Job paused successfully.',
      });
      
      return jobCardData;
    } catch (error) {
      console.error('Error pausing job:', error);
      toast({
        title: 'Error',
        description: 'Failed to pause job. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resume a job
   */
  const resumeJob = async (data: ResumeJobRequest) => {
    try {
      setLoading(true);
      const userId = user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Update the job card
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .update({
          status: 'In Progress',
        })
        .eq('id', data.job_card_id)
        .select()
        .single();
      
      if (jobCardError) {
        throw jobCardError;
      }
      
      // Record the activity
      await recordActivity({
        job_card_id: data.job_card_id,
        activity_type: 'resume',
        description: data.notes || 'Job resumed',
      });
      
      toast({
        title: 'Success',
        description: 'Job resumed successfully.',
      });
      
      return jobCardData;
    } catch (error) {
      console.error('Error resuming job:', error);
      toast({
        title: 'Error',
        description: 'Failed to resume job. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Complete a job with the enhanced flow
   */
  const completeJob = async (data: CompleteJobRequest) => {
    try {
      setLoading(true);
      const userId = user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Update the job card
      const { data: jobCardData, error: jobCardError } = await supabase
        .from('job_cards')
        .update({
          status: 'Completed',
          actual_end_time: new Date().toISOString(),
          quantity_completed: data.quantity_completed,
          scrap_quantity: data.scrap_quantity || 0
        })
        .eq('id', data.job_card_id)
        .select()
        .single();
      
      if (jobCardError) {
        throw jobCardError;
      }
      
      // Record the activity
      await recordActivity({
        job_card_id: data.job_card_id,
        activity_type: 'complete',
        description: data.notes || 'Job completed',
        quantity: data.quantity_completed,
        metadata: { 
          scrap_quantity: data.scrap_quantity 
        }
      });
      
      // Create a time entry for the completion
      if (jobCardData.actual_start_time) {
        await addTimeEntry({
          job_card_id: data.job_card_id,
          worker_id: jobCardData.worker_id,
          start_time: jobCardData.status === 'Paused' ? new Date().toISOString() : jobCardData.actual_start_time,
          end_time: new Date().toISOString(),
          duration_minutes: calculateDurationMinutes(
            jobCardData.status === 'Paused' ? new Date() : new Date(jobCardData.actual_start_time), 
            new Date()
          ),
          notes: data.notes,
          quantity_produced: data.quantity_completed
        });
      }
      
      toast({
        title: 'Success',
        description: 'Job completed successfully.',
      });
      
      return jobCardData;
    } catch (error) {
      console.error('Error completing job:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete job. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Report an issue with a job
   */
  const reportIssue = async (data: ReportIssueRequest) => {
    try {
      setLoading(true);
      const userId = user?.id;
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Record the activity
      await recordActivity({
        job_card_id: data.job_card_id,
        activity_type: 'issue',
        description: data.description,
        metadata: { 
          issue_type: data.issue_type,
          severity: data.severity,
          ...data.metadata
        }
      });
      
      toast({
        title: 'Success',
        description: 'Issue reported successfully.',
      });
      
      return true;
    } catch (error) {
      console.error('Error reporting issue:', error);
      toast({
        title: 'Error',
        description: 'Failed to report issue. Please try again.',
        variant: 'destructive'
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Record an activity for a job card
   */
  const recordActivity = async (data: RecordJobCardActivityRequest) => {
    try {
      const userId = user?.id;
      console.log('recordActivity - User ID:', userId);
      console.log('recordActivity - User object:', user);
      
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Debug session context
      const { data: { session } } = await supabase.auth.getSession();
      console.log('recordActivity - Current session:', session);
      
      const { data: activityData, error } = await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: data.job_card_id,
          user_id: userId,
          activity_type: data.activity_type,
          description: data.description,
          quantity: data.quantity,
          metadata: data.metadata
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return activityData;
    } catch (error) {
      console.error('Error recording activity:', error);
      throw error;
    }
  };

  /**
   * Fetch activities for a job card
   */
  const fetchJobCardActivities = async (jobCardId: string) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('job_card_activities_with_details')
        .select('*')
        .eq('job_card_id', jobCardId)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return data as JobCardActivity[];
    } catch (error) {
      console.error('Error fetching job card activities:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch activities. Please try again.',
        variant: 'destructive'
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  /**
   * Helper function to calculate duration in minutes between two dates
   */
  const calculateDurationMinutes = (start: Date | string, end: Date | string) => {
    const startDate = typeof start === 'string' ? new Date(start) : start;
    const endDate = typeof end === 'string' ? new Date(end) : end;
    return Math.round((endDate.getTime() - startDate.getTime()) / 60000);
  };

  /**
   * Add a time entry for a job card with enhanced properties
   */
  const addTimeEntry = async (timeEntryData: TimeEntryRequest & {
    downtime_reason?: string;
    downtime_category?: string;
    quantity_produced?: number;
  }) => {
    try {
      setLoading(true);
      
      // Don't use .single() as our database trigger might intercept and update instead of insert
      const { data, error } = await supabase
        .from('time_entries')
        .insert(timeEntryData)
        .select();
      
      if (error) {
        throw error;
      }
      
      // Success even if the data is empty (could happen if trigger updated instead)
      toast({
        title: 'Success',
        description: 'Time entry recorded successfully.',
      });
      
      // Return the first item if available (for compatibility), or an empty object
      return data && data.length > 0 ? data[0] : {};
    } catch (error) {
      console.error('Error adding time entry:', error);
      toast({
        title: 'Error',
        description: 'Failed to add time entry. Please try again.',
        variant: 'destructive'
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch job cards for a worker
   */
  const fetchJobCardsByWorker = async (workerId: string) => {
    return await fetchJobCards({ worker_id: workerId });
  };

  /**
   * Fetch job cards for a work order
   */
  const fetchJobCardsByWorkOrder = async (workOrderId: string) => {
    return await fetchJobCards({ work_order_id: workOrderId });
  };

  return {
    jobCards,
    selectedJobCard,
    loading,
    fetchJobCards,
    fetchJobCardById,
    createJobCard,
    updateJobCard,
    deleteJobCard,
    
    // Enhanced job card actions
    startJob,
    pauseJob,
    resumeJob,
    completeJob,
    reportIssue,
    recordActivity,
    fetchJobCardActivities,
    
    // Legacy methods for backward compatibility
    startJobCard: (id: string) => startJob({ job_card_id: id }),
    completeJobCard: (id: string, quantity_completed: number) => 
      completeJob({ job_card_id: id, quantity_completed }),
    
    // Time entry management
    addTimeEntry,
    
    // Filter methods
    fetchJobCardsByWorker,
    fetchJobCardsByWorkOrder
  };
};
